import { ErrorBoundary } from '@sentry/react';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import DataFetcher from 'src/utils/data-fetcher';

import { AuthGuard } from 'src/guards/auth';
import SubscriptionReviewGuard from 'src/guards/billing/subscription-guard';
import DashboardLayout from 'src/layouts/dashboard';
import SentryErrorPage from 'src/pages/SentryErrorPage';
import CandidatesPage from 'src/pages/candidates/candidates-page';
import OrganizationDocumentsPage from 'src/pages/dashboard/admin/documents/documents-page';
import OrganizationIntegrationsPage from 'src/pages/dashboard/admin/integrations/integrations-list-page';
import ContactsPage from 'src/pages/dashboard/contacts/contacts-page';
import ListsPage from 'src/pages/dashboard/lists/lists-page';
import CandidateProfileDocumentsPage from 'src/pages/dashboard/profile/candidate-profile-documents-page';
import CandidateProfileRefereesPage from 'src/pages/dashboard/profile/candidate-profile-referees-page';
import TasksPage from 'src/pages/dashboard/tasks/tasks-page';

import JoyrideContainer from 'src/components/joyride/joyride-container';
import { LoadingScreen } from 'src/components/loading-screen';

import AccountEmailConnection from 'src/sections/account/account-email-connection';
import { FileManagerView } from 'src/sections/admin/documents/view';
import IntegrationsContainer from 'src/sections/admin/integrations/integrations-container';
import IntegrationActivityView from 'src/sections/admin/integrations/view/integrations-activity-view';
import IntegrationsListView from 'src/sections/admin/integrations/view/integrations-list-view';
import IntegrationSettingsView from 'src/sections/admin/integrations/view/integrations-settings-view';
import UnmatchedActivityView from 'src/sections/admin/integrations/communications/unmatched-activity-view';
import UserMappingView from 'src/sections/admin/integrations/communications/user-mapping-view';
import DomainsListView from 'src/sections/admin/organization/domains/view/domains-list-view';
import OrganizationSettingsView from 'src/sections/admin/organization/settings/view/settings-view';
import InboundEmailActivityView from 'src/sections/admin/inbound-email/views/inbound-email-activity-view';
import { UsageGuard } from 'src/sections/billing/usage';
import CandidateDetail from 'src/sections/candidates/view/candidate-detail-view';
import CandidatesView from 'src/sections/candidates/view/candidate-list-view';
import MyCompaniesDetailView from 'src/sections/companies/detail/companies-detail-view';
import CompaniesView from 'src/sections/companies/list/companies-view';
import ContactsDetailView from 'src/sections/contacts/view/contacts-detail-view';
import ContactsListView from 'src/sections/contacts/view/contacts-list-view';
import DealDetailView from 'src/sections/deal-flow/views/deal-detail-view';
import ApplicationDetailView from 'src/sections/jobs/applications/detail/application-detail-view';
import PlacementDetailView from 'src/sections/jobs/placements/detail/placement-detail-view';
import ListsDetailView from 'src/sections/lists/detail/list-detail-view';
import CandidateProfileRefereeListView from 'src/sections/profile/references/profile-referees-list-view';

import AccountSchedulingManagement from 'src/sections/account/account-scheduling';
import CalendarView from 'src/sections/calendar/calendar-view';
import NotetakerDetailView from 'src/sections/meetings/view/notetaker-detail-view';
import NotetakerListView from 'src/sections/meetings/view/notetaker-list-view';
import PlacementsPage from 'src/pages/dashboard/placements/placements-page';
import ReportsPage from 'src/pages/dashboard/reports/reports-page';
import CandidateSearchView from 'src/sections/candidates/view/candidate-search-view';
import { paths } from '../paths';

// ----------------------------------------------------------------------

const OrganisationPage = lazy(() => import('src/pages/dashboard/organisation-page'));
const DepartmentsPage = lazy(
  () => import('src/pages/dashboard/admin/organization/department/departments-page')
);
const UsersPage = lazy(() => import('src/pages/dashboard/admin/organization/users/users-page'));
const CompanyProfilePage = lazy(
  () => import('src/pages/dashboard/admin/organization/profile/company-profile-page')
);
const BillingPage = lazy(() => import('src/pages/dashboard/admin/billing/billing-page'));
const ConnectionsPage = lazy(() => import('src/pages/dashboard/connections/connections-page'));
const CompaniesPage = lazy(() => import('src/pages/dashboard/companies/companies-page'));
const DepartmentsDetailView = lazy(
  () => import('src/sections/admin/organization/departments/view/departments-detail-view')
);
const AdminConnectionsPage = lazy(
  () => import('src/pages/dashboard/admin/connection-management/connections-management-page')
);
const AdminConnectionsView = lazy(
  () => import('src/sections/admin/connections/view/connections-view')
);
const AdminConnectionsDetailView = lazy(
  () => import('src/sections/admin/connections/view/connection-detail-view')
);
const JobsPage = lazy(() => import('src/pages/dashboard/jobs/jobs-page'));
const CreateEditJobContainer = lazy(
  () => import('src/sections/jobs/create/create-edit-job-container')
);
const JobsView = lazy(() => import('src/sections/jobs/view/jobs-view'));
const JobsDetailView = lazy(() => import('src/sections/jobs/detail/job-details-view'));
const CandidateProfilePage = lazy(
  () => import('src/pages/dashboard/profile/candidate-profile-page')
);
const CandidateProfileView = lazy(() => import('src/sections/profile/view/candidate-profile-view'));
const ConnectionsView = lazy(() => import('src/sections/connections/view/connections-view'));
const MyConnectionsDetailView = lazy(
  () => import('src/sections/connections/detail/connections-detail-view')
);

const ActivityPage = lazy(() => import('src/pages/dashboard/activity/activity-page'));
const ActivityView = lazy(() => import('src/sections/activity/view/activity-view'));

const AccountPage = lazy(() => import('src/pages/dashboard/account/account-page'));

const AccountGeneral = lazy(() => import('src/sections/account/account-general'));
const AccountNotifications = lazy(() => import('src/sections/account/account-notifications'));
const AccountChangePassword = lazy(() => import('src/sections/account/account-security'));

const TasksListView = lazy(() => import('src/sections/tasks/views/tasks-list-view'));

const DealFLowPage = lazy(() => import('src/pages/dashboard/deal-flow/deal-flow-page'));
const DealPipelineView = lazy(() => import('src/sections/deal-flow/views/deal-list-view'));

const NotetakerPage = lazy(() => import('src/pages/notetaker/notetaker-page'));

const CalendarPage = lazy(() => import('src/pages/calendar/calendar-page'));

const ReportsListView = lazy(() => import('src/sections/reports/views/reports-list-view'));
const ReportDetailView = lazy(() => import('src/sections/reports/views/report-detail-view'));

const SigningPreparePage = lazy(() => import('src/pages/dashboard/signing/signing-prepare-page'));
const SigningAdminView = lazy(() => import('src/sections/admin/organization/signing/signing-admin-view'));


// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <ErrorBoundary fallback={<SentryErrorPage />}>
        <AuthGuard>
          <UsageGuard>
            <SubscriptionReviewGuard>
              <JoyrideContainer />
              <DataFetcher />
              <DashboardLayout>
                <Suspense fallback={<LoadingScreen />}>
                  <Outlet />
                </Suspense>
              </DashboardLayout>
            </SubscriptionReviewGuard>
          </UsageGuard>
        </AuthGuard>
      </ErrorBoundary>
    ),
    children: [
      {
        element: <ActivityPage />,
        children: [
          {
            element: <Navigate to={paths.dashboard.root} replace />,
            index: true,
          },
          {
            path: 'activity',
            element: <ActivityView />,
          },
        ],
      },
      {
        path: 'jobs',
        element: <JobsPage />,
        children: [
          {
            index: true,
            path: '',
            element: <JobsView />,
          },
          {
            path: 'create',
            element: <CreateEditJobContainer />,
          },
          {
            path: ':job_id',
            element: <JobsDetailView />,
          },
          {
            path: ':job_id/editor',
            element: <CreateEditJobContainer />,
          },
          {
            path: ':job_id/applications/:application_id',
            element: <ApplicationDetailView />,
          },
        ],
      },
      {
        path: 'profile',
        element: <CandidateProfilePage />,
        children: [
          {
            index: true,
            element: <CandidateProfileView />,
          },
        ],
      },
      {
        path: 'documents',
        element: <CandidateProfileDocumentsPage />,
      },
      {
        path: 'referees',
        element: <CandidateProfileRefereesPage />,
        children: [
          {
            index: true,
            element: <CandidateProfileRefereeListView />,
          }
        ],
      },
      {
        path: 'account',
        element: <AccountPage />,
        children: [
          {
            path: '',
            index: true,
            element: <AccountGeneral />,
          },
          {
            path: 'notifications',
            element: <AccountNotifications />,
          },
          {
            path: 'security',
            element: <AccountChangePassword />,
          },
          {
            path: 'email',
            element: <AccountEmailConnection />,
          },
          {
            path: 'scheduling',
            element: <AccountSchedulingManagement />,
          },
        ],
      },
      {
        path: 'companies/all',
        element: <CompaniesPage />,
        children: [
          {
            index: true,
            path: '',
            element: <CompaniesView />,
          },
          {
            path: ':connection_id',
            element: <MyCompaniesDetailView />,
          },
        ],
      },
      {
        path: 'companies/:target_tenant',
        element: <ConnectionsPage />,
        children: [
          {
            index: true,
            path: '',
            element: <ConnectionsView />,
          },
          {
            path: ':connection_id',
            element: <MyConnectionsDetailView />,
          },
        ],
      },
      {
        path: 'contacts',
        element: <ContactsPage />,
        children: [
          {
            index: true,
            path: '',
            element: <ContactsListView />,
          },
          {
            path: ':contact_id',
            element: <ContactsDetailView />,
          }
        ],
      },
      {
        path: 'lists',
        element: <ListsPage />,
        children: [
          {
            path: ':list_id',
            element: <ListsDetailView />,
          }
        ],
      },
      {
        path: 'candidates',
        element: <CandidatesPage />,
        children: [
          {
            index: true,
            path: '',
            element: <CandidatesView />,
          },
          {
            index: true,
            path: 'search',
            element: <CandidateSearchView />,
          },
          {
            path: ':candidate_id',
            element: <CandidateDetail />,
          },
        ],
      },
      {
        path: 'tasks',
        element: <TasksPage />,
        children: [
          {
            index: true,
            path: '',
            element: <TasksListView />,
          }
        ],
      },
      {
        path: 'signing/:requestId/prepare',
        element: <SigningPreparePage />,
      },
      {
        path: 'placements',
        element: <PlacementsPage />,
        children: [
          {
            index: true,
            path: ':placement_id',
            element: <PlacementDetailView />,
          }
        ],
      },
      {
        path: 'deal-flow',
        element: <DealFLowPage />,
        children: [
          {
            index: true,
            path: '',
            element: <DealPipelineView />,
          },
          {
            path: 'deals/:deal_id',
            element: <DealDetailView />,
          }
        ],
      },
      {
        path: 'notetaker',
        element: <NotetakerPage />,
        children: [
          {
            index: true,
            path: '',
            element: <NotetakerListView />,
          },
          {
            path: ':meeting_id',
            element: <NotetakerDetailView />,
          }
        ],
      },
      {
        path: 'calendar',
        element: <CalendarPage />,
        children: [
          {
            index: true,
            path: '',
            element: <CalendarView />,
          }
        ],
      },
      {
        path: 'admin',
        children: [
          {
            path: 'organisation',
            element: <OrganisationPage />,
            children: [
              {
                element: (
                  <Navigate to={paths.dashboard.admin.organization.company_profile} replace />
                ),
                index: true,
              },
              {
                path: 'company-profile',
                element: <CompanyProfilePage />,
              },
              {
                path: 'departments',
                element: <DepartmentsPage />,
              },
              {
                path: 'users',
                element: <UsersPage />,
              },
              {
                path: 'departments/:department_id',
                element: <DepartmentsDetailView />,
              },
              {
                path: 'settings',
                element: <OrganizationSettingsView />,
              },
              {
                path: 'domains',
                element: <DomainsListView />,
              },
              {
                path: 'signing-documents',
                element: <SigningAdminView />,
              },
            ],
          },
          {
            path: 'billing',
            element: <BillingPage />,
          },
          {
            path: 'inbound-email-activity',
            element: <InboundEmailActivityView />,
          },
          {
            path: 'connections',
            element: <AdminConnectionsPage />,
            children: [
              {
                index: true,
                path: '',
                element: <AdminConnectionsView />,
              },
              {
                path: ':connection_id',
                element: <AdminConnectionsDetailView />,
              },
            ],
          },
          {
            path: 'documents',
            element: <OrganizationDocumentsPage />,
            children: [
              {
                index: true,
                path: '',
                element: <FileManagerView />,
              },
            ],
          },
          {
            path: 'integrations',
            element: <OrganizationIntegrationsPage />,
            children: [
              {
                index: true,
                path: '',
                element: <IntegrationsListView />,
              },
            ],
          },
          {
            path: 'reports',
            element: <ReportsPage />,
            children: [
              {
                index: true,
                path: '',
                element: <ReportsListView />,
              },
              {
                path: ':report_type',
                element: <ReportDetailView />,
              }
            ],
          },
          {
            path: 'integrations/:integration_id',
            element: <IntegrationsContainer />,
            children: [
              {
                index: true,
                path: 'configuration',
                element: <IntegrationSettingsView />,
              },
              {
                index: true,
                path: 'activity',
                element: <IntegrationActivityView />,
              },
              {
                path: 'communications',
                element: <UnmatchedActivityView />,
              },
              {
                path: 'user-mapping',
                element: <UserMappingView />,
              },
            ],
          },
        ],
      },
    ],
  },
];
